import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Box,
  Typography,
  Paper,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BadgeImage from "../../../components/badges_image/badgesImage";

// Border color
const borderColor = "#CCCCCC";

// Table container with shadow and rounded corners
const TableWrapper = styled(Paper)({
  borderRadius: 16,
  backgroundColor: "#fff",
  overflowX: "auto",
  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
});

// Table styling
const StyledTable = styled(Table)({
  minWidth: 650,
  borderCollapse: "separate",
  borderSpacing: 0,
});

// Header cells
const HeaderCell = styled(TableCell)({
  backgroundColor: "var(--org-bg-color, #f6615f)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 16,
  textAlign: "center",
  borderRight: `1px solid ${borderColor}`,
  padding: "18px",
});

// Body cells
const StyledTableCell = styled(
  TableCell,
  {
    shouldForwardProp: (prop) =>
      !["isLastRow", "isLastCol", "isFirstCol", "isFirstRow"].includes(prop),
  }
)(({ isLastRow, isLastCol, isFirstCol, isFirstRow }) => ({
  backgroundColor: "#fff",
  color: "#000",
  fontSize: 16,
  textAlign: "center",
  padding: "10px 16px",
  borderBottom: !isLastRow ? `1px solid ${borderColor}` : "none",
  borderRight: !isLastCol ? `1px solid ${borderColor}` : "none",
  borderLeft: isFirstCol ? "none" : undefined,
  borderTop: isFirstRow ? "none" : undefined,
}));

const LeaderboardEmployeeTable = ({ columns, rows, loading }) => {
  const rowsPerPage = 10;
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(rows.length / rowsPerPage);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const paginatedRows = rows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <TableWrapper elevation={0}>
      <StyledTable>
        <TableHead>
          <TableRow>
            {columns.map((col, colIdx) => (
              <HeaderCell
                key={col.id}
                sx={{
                  borderLeft: colIdx === 0 ? "none" : undefined,
                  borderRight:
                    colIdx === columns.length - 1 ? "none" : undefined,
                }}
              >
                {col.label}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Box
                  height="64vh"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <div className="spinner-border text-danger" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </Box>
              </TableCell>
            </TableRow>
          ) : rows.length > 0 ? (
            paginatedRows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {columns.map((col, colIdx) => {
                  const value = row[col.id];
                  const isLastRow = rowIdx === paginatedRows.length - 1;
                  const isLastCol = colIdx === columns.length - 1;
                  const isFirstCol = colIdx === 0;
                  const isFirstRow = rowIdx === 0;

                  if (col.id === "employee") {
                    return (
                      <StyledTableCell
                        key={col.id}
                        isLastRow={isLastRow}
                        isLastCol={isLastCol}
                        isFirstCol={isFirstCol}
                        isFirstRow={isFirstRow}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={value.profile_image || ""} alt={value.name} />
                          <Typography fontSize={16}>{value.name}</Typography>
                        </Box>
                      </StyledTableCell>
                    );
                  }

                  if (col.id === "badges") {
                    return (
                      <StyledTableCell
                        key={col.id}
                        isLastRow={isLastRow}
                        isLastCol={isLastCol}
                        isFirstCol={isFirstCol}
                        isFirstRow={isFirstRow}
                      >
                        <Box display="flex" justifyContent="center" gap={2}>
                          {value.map((badges, badgeIdx) => (
                            <Box
                              key={badgeIdx}
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                            >
                              <BadgeImage
                                badgeName={(badges.badgeName || "default").toLowerCase()}
                                altText={badges.badgeName || "Default Badge"}
                                width={50}
                                height={50}
                              />
                              <Typography fontSize={14}>
                                {badges.count}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </StyledTableCell>
                    );
                  }

                  if (col.id === "readings") {
                    return (
                      <StyledTableCell
                        key={col.id}
                        isLastRow={isLastRow}
                        isLastCol={isLastCol}
                        isFirstCol={isFirstCol}
                        isFirstRow={isFirstRow}
                      >
                        {value}{" "}
                        {`${value === 1 || value === 0 ? "card" : "cards"
                          } completed`}
                      </StyledTableCell>
                    );
                  }

                  if (col.id === "total_points") {
                    return (
                      <StyledTableCell
                        key={col.id}
                        isLastRow={isLastRow}
                        isLastCol={isLastCol}
                        isFirstCol={isFirstCol}
                        isFirstRow={isFirstRow}
                      >
                        {value}{" "}
                        {`${value === 1 || value === 0 ? "Point" : "Points"}`}
                      </StyledTableCell>
                    );
                  }

                  return (
                    <StyledTableCell
                      key={col.id}
                      isLastRow={isLastRow}
                      isLastCol={isLastCol}
                      isFirstCol={isFirstCol}
                      isFirstRow={isFirstRow}
                    >
                      {value}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Box
                  height="64vh"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <p style={{ fontSize: "18px" }}>
                    No employee data available.
                  </p>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>

      {/* Pagination Controls */}
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" my={3}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={handleChangePage}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                backgroundColor: "#fff",
                borderRadius: "8px",
              },
              "& .Mui-selected": {
                backgroundColor: "var(--org-bg-color, #f6615f) !important",
                color: "#fff !important",
                "&:hover": {
                  backgroundColor: "var(--org-bg-color, #f6615f) !important",
                },
              },
            }}
          />
        </Box>
      )}
    </TableWrapper>
  );
};

export default LeaderboardEmployeeTable;
